import React, { Component, type ReactNode } from 'react';
import flow from 'lodash/flow';
import noop from 'lodash/noop';
import type AnalyticsWebClient from '@atlassiansox/analytics-web-client';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type Client from '@atlaskit/pubsub';
import type { PubSubClient } from '@atlaskit/pubsub';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { AnalyticsEventToProps } from '@atlassian/jira-product-analytics-bridge';
import { getAnalyticsWebClientPromise } from '@atlassian/jira-product-analytics-web-client-async';
import { fg } from '@atlassian/jira-feature-gating';
import { PUBSUB_DEFAULT_HOST, PUBSUB_PRODUCT_JIRA } from '../../common/constants/pubsub.tsx';
import { Provider } from '../../common/utils/context.utils.tsx';
import { analyticEvents, analyticsComponent } from '../../common/constants/analyticEvents.tsx';
import { SOURCE_FPS } from '../../common/constants/sources.tsx';
import { withHost } from './with-host/index.tsx';
import { withRuntimeProvider } from './with-runtime-provider/index.tsx';

const LOG_LOCATION = 'jira-realtime-provider-fps';

const createPubSubClient = (host: string, analyticsClient?: AnalyticsWebClient) =>
	import(/* webpackChunkName: "@atlaskit-jira_pubsub" */ '@atlaskit/pubsub').then((module) => {
		const Client = module.default;

		return new Client({
			product: PUBSUB_PRODUCT_JIRA,
			url: `${host}`,
			apsProtocol: {
				enabled: true,
			},
			analyticsClient,
		});
	});

export const joinChannel = (
	appId: string,
	client: Client,
	channels: string[],
	onHandler: (result?: PubSubClient) => void,
	onError?: (error: Error) => void,
) =>
	client
		.join(channels)
		.then(onHandler) // eslint-disable-next-line @typescript-eslint/no-explicit-any
		.catch((error: any) => {
			// Ideally anonymous users shouldn't get there.
			// Ignoring 401 status codes until it is fixed on the app level.
			if (error && error.code === 401) {
				return;
			}
			log.safeErrorWithoutCustomerData(LOG_LOCATION, `[${appId}]: Failed to join channel`, error);
			if (fg('jirt-convert-class-to-functional-component')) {
				onError?.(error);
			}
		});

export const leaveChannel = (
	appId: string,
	client: Client,
	channels: string[],
	onHandler: (result?: PubSubClient) => void,
	onError?: (error: Error) => void,
) =>
	client
		.leave(channels)
		.then(onHandler) // eslint-disable-next-line @typescript-eslint/no-explicit-any
		.catch((error: any) => {
			// Ideally anonymous users shouldn't get there.
			// Ignoring 401 status codes until it is fixed on the app level.
			if (error && error.code === 401) {
				return;
			}
			log.safeErrorWithoutCustomerData(LOG_LOCATION, `[${appId}]: Failed to left channel`, error);
			if (fg('jirt-convert-class-to-functional-component')) {
				onError?.(error);
			}
		});

type DefaultProps = {
	host: string;
	onJoin: (channels: string[], arg2: UIAnalyticsEvent) => void;
	onLeave: (channels: string[], arg2: UIAnalyticsEvent) => void;
};

type Props = DefaultProps & {
	appId: string;
	channels: string[];
	onError?: (error: Error) => void;
	children: ReactNode;
};

type State = {
	client: Client | null;
};

// eslint-disable-next-line jira/react/no-class-components
class RealtimeProviderView extends Component<Props, State> {
	static defaultProps: DefaultProps = {
		host: PUBSUB_DEFAULT_HOST,
		onJoin: noop,
		onLeave: noop,
	};

	state = { client: null };

	componentDidMount() {
		const { appId, host, channels, onError } = this.props;

		this.isComponentMounted = true;

		getAnalyticsWebClientPromise()
			.then((analyticsClient) => createPubSubClient(host, analyticsClient.getInstance()))
			.then((pubSubClient) =>
				joinChannel(appId, pubSubClient, channels, this.onJoinChannel, onError).then(() => {
					if (this.isComponentMounted) {
						this.setState({ client: pubSubClient });
					}
				}),
			);
	}

	componentDidUpdate({ channels: prevChannels }: Props) {
		const { appId, channels: newChannels, onError } = this.props;
		const { client } = this.state;

		if (client && newChannels !== prevChannels) {
			const removedChannels = prevChannels.filter((channel) => !newChannels.includes(channel));
			if (fg('jirt-convert-class-to-functional-component')) {
				if (removedChannels.length > 0) {
					leaveChannel(appId, client, removedChannels, this.onLeaveChannel, onError);
					joinChannel(appId, client, newChannels, this.onJoinChannel, onError);
				}
			} else {
				leaveChannel(appId, client, removedChannels, this.onLeaveChannel, onError);
				joinChannel(appId, client, newChannels, this.onJoinChannel, onError);
			}
		}
	}

	componentWillUnmount() {
		const { appId, channels, onError } = this.props;
		const { client } = this.state;

		this.isComponentMounted = false;

		if (client) {
			leaveChannel(appId, client, channels, this.onLeaveChannel, onError);
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onJoinChannel = (result: any) => {
		const { onJoin } = this.props;
		if (fg('jirt-convert-class-to-functional-component')) {
			log.safeInfoWithoutCustomerData(LOG_LOCATION, 'Joined channel', {
				source: SOURCE_FPS,
				appId: this.props?.appId,
				channels: result?.channels?.join(', ') ?? '',
			});
		}
		// @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
		onJoin(Array.from(result?.channels ?? []));
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onLeaveChannel = (result: any) => {
		const { onLeave } = this.props;
		if (fg('jirt-convert-class-to-functional-component')) {
			log.safeInfoWithoutCustomerData(LOG_LOCATION, 'Left channel', {
				source: SOURCE_FPS,
				appId: this.props?.appId,
				channels: result?.channels?.join(', ') ?? '',
			});
		}
		// @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
		onLeave(Array.from(result?.channels ?? []));
	};

	isComponentMounted = false;

	render() {
		return (
			<Provider
				value={{
					client: this.state.client,
				}}
			>
				{this.props.children}
			</Provider>
		);
	}
}

const withAnalytics = AnalyticsEventToProps(analyticsComponent, {
	onJoin: analyticEvents.JOINED_CHANNEL,
	onLeave: analyticEvents.LEFT_CHANNEL,
});

export const RealtimeProvider = flow(
	withAnalytics,
	withHost,
	withRuntimeProvider,
)(RealtimeProviderView);

export default RealtimeProvider;
